import React, { useEffect, useState } from "react";

import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { CustomInvokeCodeEditor } from "src/aiNode/CustomInvokeCodeEditor";
import { EnvironmentConfig } from "src/aiNode/editorComponents/EnvironmentConfig";
import {
  AiNodeV2Form,
  BasicModelCard,
  ModelCapabilities,
  REASONING_EFFORT_LEVEL_LABELS,
  ReasoningEffortLevel,
} from "src/aiNode/types";
import { FieldErrors } from "src/api/types";
import { Divider } from "src/base-components/Divider";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Switch } from "src/base-components/Switch";
import { TagsInput } from "src/base-components/TagsInput";
import { TimeoutInput } from "src/baseConnectionNode/TimeoutInput";
import {
  handleIntegerInput,
  handleNumericInput,
} from "src/baseConnectionNode/utils";
import { AiNodeV2ResponseFormatConfigTypeEnum } from "src/clients/flow-api";
import { Tooltip } from "src/design-system/Tooltip";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

const SYSTEM_PROMPT_PLACEHOLDER =
  "e.g. You are a specialized credit risk analyst working within a regulated financial institution. Your role is to analyse $data.applicant_attribute of credit applications.";

const STOP_SEQUENCE_DESCRIPTION =
  "Specify text patterns that signal the AI to stop generating output. You can add up to four stop sequences.";
const MAX_STOP_SEQUENCE_ITEMS = 4;

const TEMPERATURE_DESCRIPTION = (minValue: number, maxValue: number) =>
  `Adjust the level of randomness in the AI's output. Lower values (closer to ${minValue}) create precise and consistent responses, while higher values (closer to ${maxValue}) make them more creative and varied.`;
const DEFAULT_MIN_TEMPERATURE_VALUE = 0;
const DEFAULT_MAX_TEMPERATURE_VALUE = 1;

const MAX_TOKENS_DESCRIPTION =
  "Limit the length of the AI's response. Higher values allow for longer outputs but may increase response times.";

const REASONING_EFFORT_DESCRIPTION =
  "Control the depth of reasoning the AI applies to complex problems. Higher levels may provide better results but will take longer and consume more tokens.";

const GLOBAL_ENDPOINT_DESCRIPTION =
  "By default, OpenAI requests follow data residency and are routed to a regional endpoint based on your workspace region. Enable this to send requests to OpenAI's global endpoint instead.";

type AdvancedConfigProps = {
  models: BasicModelCard[];
  immutable: boolean;
  isReactive: boolean;
  runFieldErrors?: FieldErrors;
};

type InferenceConfigProps = {
  immutable: boolean;
  isReactive: boolean;
  runFieldErrors?: FieldErrors;
  modelCapabilities?: ModelCapabilities;
  isOutputStructured: boolean;
};

type SystemPromptConfigProps = {
  immutable: boolean;
  isReactive: boolean;
  runFieldErrors?: FieldErrors;
};

const SystemPromptConfig: React.FC<SystemPromptConfigProps> = ({
  immutable,
  isReactive,
  runFieldErrors,
}) => {
  const { watch, control, getValues } = useFormContext<AiNodeV2Form>();
  const systemPromptEnabled = watch("prompts.system_prompt.active");

  const errorMessage = runFieldErrors?.[getValues("prompts.system_prompt.id")];

  return (
    <div data-loc="ai-node-config-system-prompt">
      <div className="items-top flex justify-between gap-x-2">
        <div>
          <h3 className="font-inter-semibold-13px text-gray-800">
            Add system prompt
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            Provide additional context or a role to guide how the AI approaches
            the task. Use '$' to include Flow data (e.g. '$data.credit_report')
            from your Decision Flow.
          </span>
        </div>
        <div>
          <Controller
            control={control}
            name="prompts.system_prompt.active"
            render={({ field: controlledField }) => (
              <Switch
                dataLoc="ai-node-config-system-prompt-toggle"
                disabled={immutable}
                enabled={controlledField.value}
                onChange={() => {
                  controlledField.onChange(!controlledField.value);
                }}
              />
            )}
          />
        </div>
      </div>
      {systemPromptEnabled && (
        <div className={twJoin(errorMessage && "border-red-400")}>
          <Controller
            control={control}
            name="prompts.system_prompt.expression"
            render={({ field }) => (
              <div className="mt-2 h-80">
                <CustomInvokeCodeEditor
                  errorMessage={errorMessage}
                  immutable={immutable}
                  isReactive={isReactive}
                  placeholder={SYSTEM_PROMPT_PLACEHOLDER}
                  searchHighlightKey={getValues("prompts.system_prompt.id")}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                />
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

const getReasoningEffortOptions = (
  reasoningEffortLevels: ReasoningEffortLevel[],
) => {
  return reasoningEffortLevels.map((level) => ({
    key: level,
    value: REASONING_EFFORT_LEVEL_LABELS[level],
  }));
};

const ReasoningEffortConfig: React.FC<
  Omit<InferenceConfigProps, "isReactive" | "isOutputStructured">
> = ({ immutable, modelCapabilities }) => {
  const { control, watch, setValue } = useFormContext<AiNodeV2Form>();

  const currentValue = watch("inference.reasoning_effort") as
    | ReasoningEffortLevel
    | undefined
    | null;
  const reasoningEffortCapability = modelCapabilities?.reasoning;
  const reasoningEffortSupportedByModel = Boolean(
    reasoningEffortCapability?.supported,
  );

  const reasoningEffortLevels =
    reasoningEffortCapability?.available_effort_levels;

  useEffect(() => {
    if (immutable || currentValue == null || !reasoningEffortSupportedByModel) {
      // Either select is immutable (published/locked flow)
      // or value is not set yet
      // or reasoning effort is not supported by model
      return;
    }

    if (reasoningEffortLevels?.includes(currentValue)) {
      // Value is still valid
      return;
    }
    // Drop the value and let the user select a new one
    setValue("inference.reasoning_effort", undefined);
  }, [
    currentValue,
    reasoningEffortLevels,
    setValue,
    immutable,
    reasoningEffortSupportedByModel,
  ]);

  if (!reasoningEffortSupportedByModel) {
    // Hide reasoning effort config if model does not support it
    return null;
  }

  const options = getReasoningEffortOptions(reasoningEffortLevels ?? []);

  return (
    <div data-loc="ai-node-config-reasoning-effort">
      <FormItem>
        <FormItem.Label description={REASONING_EFFORT_DESCRIPTION}>
          Reasoning effort
        </FormItem.Label>
        <Controller
          control={control}
          name="inference.reasoning_effort"
          render={({ field: controlledField }) => (
            <div className="w-full">
              <FixedSimpleDropdown
                buttonDataLoc="ai-node-config-reasoning-effort-dropdown-button"
                dataLoc="ai-node-config-reasoning-effort-dropdown"
                disabled={immutable}
                elements={options}
                itemsWidth="w-full"
                placeholder="Select reasoning effort"
                selectedKey={controlledField.value}
                onSelect={controlledField.onChange}
              />
            </div>
          )}
        />
      </FormItem>
    </div>
  );
};

export const GlobalEndpointConfig: React.FC<
  Omit<
    InferenceConfigProps,
    "isReactive" | "isOutputStructured" | "modelCapabilities"
  >
> = ({ immutable }) => {
  const { control, watch } = useFormContext<AiNodeV2Form>();

  const provider = watch("model.provider");
  const isOpenAi = provider === "openai";
  const currentValue = watch("inference.use_global_endpoint");

  // Gate behind a feature flag, but keep showing the toggle for nodes that
  // already have it enabled, so turning the flag off never orphans a stored
  // `true` behind a hidden control.
  const isGlobalEndpointVisible =
    isFeatureFlagEnabled(FEATURE_FLAGS.aiNodeGlobalEndpoint) ||
    Boolean(currentValue);

  if (!isGlobalEndpointVisible || !isOpenAi) {
    return null;
  }

  return (
    <div data-loc="ai-node-config-global-endpoint">
      <div className="items-top flex justify-between gap-x-2">
        <div>
          <h3 className="font-inter-semibold-13px text-gray-800">
            Use global endpoint
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            {GLOBAL_ENDPOINT_DESCRIPTION}
          </span>
        </div>
        <Controller
          control={control}
          name="inference.use_global_endpoint"
          render={({ field: controlledField }) => (
            <Switch
              dataLoc="ai-node-config-global-endpoint-toggle"
              disabled={immutable}
              enabled={Boolean(controlledField.value)}
              onChange={() => {
                controlledField.onChange(!controlledField.value);
              }}
            />
          )}
        />
      </div>
    </div>
  );
};

const StopSequenceConfig: React.FC<
  Omit<InferenceConfigProps, "isReactive">
> = ({ immutable, isOutputStructured, modelCapabilities }) => {
  const { control } = useFormContext<AiNodeV2Form>();

  const stopSequencesCapability = modelCapabilities?.stop_sequences;
  const stopSequencesSupportedByModel = Boolean(
    stopSequencesCapability?.supported,
  );

  if (!stopSequencesSupportedByModel) {
    // Hide stop sequences config if model does not support it
    return null;
  }

  const tooltipText = isOutputStructured
    ? 'You cannot configure "Stop sequences" if "Structured output" is enabled'
    : "";

  return (
    <div data-loc="ai-node-config-stop-sequence">
      <FormItem>
        <FormItem.Label description={STOP_SEQUENCE_DESCRIPTION}>
          Stop sequences
        </FormItem.Label>
        <Controller
          control={control}
          name="inference.stop_sequences"
          render={({ field: controlledField }) => (
            <Tooltip disabled={!isOutputStructured} title={tooltipText} asChild>
              <div className="w-full">
                <TagsInput
                  disabled={immutable || isOutputStructured}
                  maxItems={MAX_STOP_SEQUENCE_ITEMS}
                  placeholder={
                    isOutputStructured ? "N/A" : "e.g. '<|endoftext|>'"
                  }
                  value={
                    isOutputStructured ? [] : (controlledField.value ?? [])
                  }
                  useLockNodeEditorClassName
                  onChange={controlledField.onChange}
                />
              </div>
            </Tooltip>
          )}
        />
      </FormItem>
    </div>
  );
};

const MaxTokensConfig: React.FC<Omit<InferenceConfigProps, "isReactive">> = ({
  immutable,
  isOutputStructured,
}) => {
  const { control, getValues } = useFormContext<AiNodeV2Form>();

  const currentValue = getValues("inference.max_tokens");
  const [isErrored, setIsErrored] = useState(
    currentValue != null && currentValue < 1,
  );

  return (
    <div data-loc="ai-node-config-max-tokens">
      <FormItem>
        <FormItem.Label description={MAX_TOKENS_DESCRIPTION}>
          Max tokens
        </FormItem.Label>
        <Controller
          control={control}
          name="inference.max_tokens"
          render={({ field: controlledField }) => {
            const showTooltip = isOutputStructured || isErrored;
            const tooltipText = isOutputStructured
              ? "The configured value must be an integer greater than zero"
              : 'You cannot configure "Max tokens" if "Structured output" is enabled';

            return (
              <Tooltip disabled={!showTooltip} title={tooltipText} asChild>
                <Input
                  disabled={immutable || isOutputStructured}
                  errored={isErrored}
                  placeholder={isOutputStructured ? "N/A" : "e.g. 1000"}
                  suffixIcon={
                    isErrored
                      ? {
                          icon: faExclamationTriangle,
                          variant: "danger",
                        }
                      : undefined
                  }
                  value={
                    isOutputStructured ? "" : (controlledField.value ?? "")
                  }
                  fullWidth
                  onChange={(event) => {
                    const newValue = event.target.value;

                    if (newValue === "") {
                      controlledField.onChange(null);
                      setIsErrored(false);
                      return;
                    }

                    const parsedNewValue = Number(newValue);
                    controlledField.onChange(parsedNewValue);
                    setIsErrored(parsedNewValue < 1);
                  }}
                  onKeyDown={handleIntegerInput}
                />
              </Tooltip>
            );
          }}
        />
      </FormItem>
    </div>
  );
};

const TemperatureConfig: React.FC<Omit<InferenceConfigProps, "isReactive">> = ({
  immutable,
  modelCapabilities,
}) => {
  const { control, getValues } = useFormContext<AiNodeV2Form>();

  const temperatureCapability = modelCapabilities?.temperature;
  const temperatureSupportedByModel = Boolean(temperatureCapability?.supported);

  const currentValue = getValues("inference.temperature");
  const minValue =
    temperatureCapability?.min_value ?? DEFAULT_MIN_TEMPERATURE_VALUE;
  const maxValue =
    temperatureCapability?.max_value ?? DEFAULT_MAX_TEMPERATURE_VALUE;
  const [isErrored, setIsErrored] = useState(
    currentValue != null &&
      (currentValue < minValue || currentValue > maxValue),
  );

  if (!temperatureSupportedByModel) {
    // Hide temperature config if model does not support it
    return null;
  }

  return (
    <div data-loc="ai-node-config-temperature">
      <FormItem>
        <FormItem.Label
          description={TEMPERATURE_DESCRIPTION(minValue, maxValue)}
        >
          Temperature
        </FormItem.Label>
        <Controller
          control={control}
          name="inference.temperature"
          render={({ field: controlledField }) => {
            return (
              <Tooltip
                disabled={isErrored}
                title={`The configured value must be a number between ${minValue} and ${maxValue}`}
                asChild
              >
                <Input
                  disabled={immutable}
                  errored={isErrored}
                  placeholder="e.g. 0.5"
                  suffixIcon={
                    isErrored
                      ? {
                          icon: faExclamationTriangle,
                          variant: "danger",
                        }
                      : undefined
                  }
                  value={controlledField.value ?? ""}
                  fullWidth
                  onChange={(event) => {
                    const newValue = event.target.value;

                    // If the input is empty, clear the field and remove any error state
                    if (newValue === "") {
                      controlledField.onChange(null);
                      setIsErrored(false);
                      return;
                    }

                    // If the input ends with a ".", allow the user to type decimal numbers like "0." and show an error (incomplete number)
                    if (newValue.slice(-1) === ".") {
                      controlledField.onChange(newValue);
                      setIsErrored(true);
                      return;
                    }

                    // If the input matches a pattern like "1.00", allow it and do not show an error
                    if (/^\d\.0+$/.test(newValue)) {
                      controlledField.onChange(newValue);
                      setIsErrored(false);
                      return;
                    }

                    // For all other cases, parse the input as a number
                    const parsedNewValue = Number(newValue);
                    controlledField.onChange(parsedNewValue);

                    // Set error state if the value is outside the allowed min/max range
                    setIsErrored(
                      parsedNewValue < minValue || parsedNewValue > maxValue,
                    );
                  }}
                  onKeyDown={handleNumericInput}
                />
              </Tooltip>
            );
          }}
        />
      </FormItem>
    </div>
  );
};

const JsonExtractionRepromptsConfig: React.FC<
  Omit<InferenceConfigProps, "isReactive">
> = ({ immutable, isOutputStructured }) => {
  const { control, getValues, setValue, trigger } =
    useFormContext<AiNodeV2Form>();
  const formLocation = "inference.json_extraction_max_reprompts";
  const currentValue = getValues(formLocation);
  const [isActive, setIsActive] = useState(
    currentValue != null && currentValue > 0,
  );

  const handleToggle = (checked: boolean) => {
    setIsActive(checked);
    if (!checked) {
      setValue(formLocation, 0);
      trigger(formLocation); // revalidate
    }
  };

  if (!isOutputStructured) {
    return null;
  }

  return (
    <div data-loc="ai-node-config-json-extraction-reprompts">
      <div className="items-top flex justify-between gap-x-2">
        <div>
          <h3 className="font-inter-semibold-13px text-gray-800">
            Enhance output schema compliance
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            Improve structured output format compliance by running the model
            multiple times. This will lead to higher cost and latency.
          </span>
        </div>
        <Switch
          dataLoc="ai-node-config-json-extraction-reprompts-toggle"
          disabled={immutable}
          enabled={isActive}
          onChange={handleToggle}
        />
      </div>
      {isActive && (
        <div className="mt-2 flex w-full flex-row items-center rounded-lg bg-gray-50 px-5 py-4">
          <div className="flex-1 font-inter-normal-12px text-gray-500">
            Number of retries before errors or failure
          </div>
          <Controller
            control={control}
            name={formLocation}
            render={({ field: controlledField, fieldState }) => {
              return (
                <Input
                  disabled={immutable}
                  errored={!!fieldState.error}
                  placeholder="between 1 and 5"
                  suffixIcon={
                    fieldState.error
                      ? {
                          icon: faExclamationTriangle,
                          variant: "danger",
                          helpText: fieldState.error.message,
                        }
                      : undefined
                  }
                  value={controlledField.value ?? ""}
                  onChange={(event) => {
                    const newValue = event.target.value;

                    if (newValue === "") {
                      controlledField.onChange(null);
                      return;
                    }

                    const parsedNewValue = Number(newValue);
                    controlledField.onChange(parsedNewValue);
                  }}
                  onKeyDown={handleIntegerInput}
                />
              );
            }}
            rules={{
              min: {
                value: 0,
                message: "Must be at least 0",
              },
              max: {
                value: 5,
                message: "Must be at most 5",
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

const InferenceConfig: React.FC<InferenceConfigProps> = ({
  immutable,
  isReactive,
  modelCapabilities,
  isOutputStructured,
  runFieldErrors,
}) => {
  return (
    <div className="flex w-full flex-col gap-y-4">
      <SystemPromptConfig
        immutable={immutable}
        isReactive={isReactive}
        runFieldErrors={runFieldErrors}
      />
      <ReasoningEffortConfig
        immutable={immutable}
        modelCapabilities={modelCapabilities}
      />
      <StopSequenceConfig
        immutable={immutable}
        isOutputStructured={isOutputStructured}
        modelCapabilities={modelCapabilities}
      />
      <MaxTokensConfig
        immutable={immutable}
        isOutputStructured={isOutputStructured}
      />
      <TemperatureConfig
        immutable={immutable}
        isOutputStructured={isOutputStructured}
        modelCapabilities={modelCapabilities}
      />
      <GlobalEndpointConfig immutable={immutable} />
    </div>
  );
};

const RequestTimeoutConfig: React.FC<
  Omit<AdvancedConfigProps, "isReactive" | "models" | "runFieldErrors">
> = ({ immutable }) => {
  const formProps = useFormContext<AiNodeV2Form>();
  const { control, getValues } = formProps;
  const isTimeoutActive = getValues("config.timeout.active");

  return (
    <div data-loc="ai-node-config-timeout">
      <div className="items-top flex justify-between gap-x-2">
        <div>
          <h3 className="font-inter-semibold-13px text-gray-800">
            Use custom request timeout
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            Enter the maximum number of seconds before timing out the request
          </span>
        </div>
        <Controller
          control={control}
          name="config.timeout.active"
          render={({ field: controlledField }) => (
            <Switch
              dataLoc="ai-node-config-timeout-toggle"
              disabled={immutable}
              enabled={controlledField.value}
              onChange={() => {
                controlledField.onChange(!controlledField.value);
              }}
            />
          )}
        />
      </div>
      {isTimeoutActive && (
        <div className="mt-2 flex w-full flex-row items-center justify-between gap-x-2 rounded-lg bg-gray-50 px-5 py-4">
          <div className="font-inter-normal-12px text-gray-500">
            Time out after (seconds)
          </div>
          <TimeoutInput
            dataLoc="ai-node-timeout-seconds"
            formProps={formProps.register("config.timeout.timeout_seconds")}
            immutable={immutable}
            unsetValue={null}
          />
        </div>
      )}
    </div>
  );
};

const SkipOnErrorConfig: React.FC<
  Omit<AdvancedConfigProps, "isReactive" | "models" | "runFieldErrors">
> = ({ immutable }) => {
  const formProps = useFormContext<AiNodeV2Form>();
  const { control } = formProps;

  return (
    <div data-loc="ai-node-config-skip-on-error">
      <div className="items-top flex justify-between gap-x-2">
        <div>
          <h3 className="font-inter-semibold-13px text-gray-800">
            Skip AI Node on error
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            In case the AI Node times out, your usage limits are exhausted, or
            the structured output fails to comply with the given schema, the
            flow will continue executing with all AI Node response fields set to
            `None`
          </span>
        </div>
        <Controller
          control={control}
          name="config.error.allow_errors"
          render={({ field: controlledField }) => (
            <Switch
              dataLoc="ai-node-config-skip-on-error-toggle"
              disabled={immutable}
              enabled={controlledField.value}
              onChange={() => {
                controlledField.onChange(!controlledField.value);
              }}
            />
          )}
        />
      </div>
    </div>
  );
};

export const AdvancedConfigV2: React.FC<AdvancedConfigProps> = ({
  models,
  immutable,
  isReactive,
  runFieldErrors,
}) => {
  const { getValues, watch, trigger } = useFormContext<AiNodeV2Form>();
  const responseType = getValues("inference.response_format.type");
  const selectedModelId = watch("model.model_id");

  useEffect(() => {
    // revalidate all config fields on mount
    trigger("inference");
    trigger("config");
  }, [trigger]);

  const selectedModelCapabilities = models.find(
    (model) => model.model_id === selectedModelId,
  )?.capabilities;

  const isOutputStructured =
    responseType === AiNodeV2ResponseFormatConfigTypeEnum.JSON;

  return (
    <div className="flex w-full flex-col gap-y-4">
      <InferenceConfig
        immutable={immutable}
        isOutputStructured={isOutputStructured}
        isReactive={isReactive}
        modelCapabilities={selectedModelCapabilities}
        runFieldErrors={runFieldErrors}
      />
      <Divider spacing="my-2" />
      <EnvironmentConfig disabled={immutable} />
      <Divider spacing="my-2" />
      <div className="flex w-full flex-col gap-y-4">
        {/* Execution config */}
        <RequestTimeoutConfig immutable={immutable} />
        <JsonExtractionRepromptsConfig
          immutable={immutable}
          isOutputStructured={isOutputStructured}
        />
        <SkipOnErrorConfig immutable={immutable} />
      </div>
    </div>
  );
};
